/**
 * /admin/lester — conversation log + teaching notes for the Lester chatbot.
 * Review what visitors ask and how Lester answers; add teaching notes to
 * correct or enrich his knowledge (they're injected as ground truth).
 */
import React, { useEffect, useState } from 'react';
import { GraduationCap, Globe, MessageCircle, Trash2 } from 'lucide-react';
import api from '../lib/api';

function CreateEventForm({ find, onDone }) {
  const [form, setForm] = useState({
    title: '', start_date: '', end_date: '', venue: '', town: '',
    url: (find.sources || [])[0] || '', summary: (find.answer || '').slice(0, 240),
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async () => {
    setBusy(true); setErr('');
    try {
      await api.post(`/admin/lester/external-finds/${find.id}/create-event`, {
        ...form,
        end_date: form.end_date || null, venue: form.venue || null,
        town: form.town || null, url: form.url || null, summary: form.summary || null,
      });
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'string' ? d
        : Array.isArray(d) ? d.map((it) => it?.msg || JSON.stringify(it)).join('; ')
        : 'Could not create the event';
      setErr(msg);
    }
    setBusy(false);
  };
  return (
    <div className="mt-3 bg-admin rounded-xl p-3 space-y-2" data-testid={`find-event-form-${find.id}`}>
      <div className="grid grid-cols-2 gap-2">
        <input value={form.title} onChange={set('title')} placeholder="Event title *" data-testid="find-event-title"
          className="col-span-2 bg-white border border-charcoal/15 rounded-lg px-3 py-1.5 text-sm" />
        <input type="date" value={form.start_date} onChange={set('start_date')} data-testid="find-event-start"
          className="bg-white border border-charcoal/15 rounded-lg px-3 py-1.5 text-sm" />
        <input type="date" value={form.end_date} onChange={set('end_date')} title="End date (optional)"
          className="bg-white border border-charcoal/15 rounded-lg px-3 py-1.5 text-sm" />
        <input value={form.venue} onChange={set('venue')} placeholder="Venue"
          className="bg-white border border-charcoal/15 rounded-lg px-3 py-1.5 text-sm" />
        <input value={form.town} onChange={set('town')} placeholder="Town"
          className="bg-white border border-charcoal/15 rounded-lg px-3 py-1.5 text-sm" />
        <input value={form.url} onChange={set('url')} placeholder="Event URL"
          className="col-span-2 bg-white border border-charcoal/15 rounded-lg px-3 py-1.5 text-sm" />
        <textarea value={form.summary} onChange={set('summary')} rows={2} maxLength={240} placeholder="Summary"
          className="col-span-2 bg-white border border-charcoal/15 rounded-lg px-3 py-1.5 text-sm" />
      </div>
      {err && <p className="text-xs text-heritage">{err}</p>}
      <div className="flex gap-2">
        <button onClick={submit} disabled={busy || !form.title || !form.start_date} data-testid="find-event-submit"
          className="bg-forest text-base text-xs font-semibold px-4 py-1.5 rounded-full disabled:opacity-50">
          {busy ? 'Creating…' : 'Create pending event'}
        </button>
        <button onClick={onDone} className="text-xs text-muted px-2">Cancel</button>
      </div>
    </div>
  );
}

function ExternalFindsPanel() {
  const [items, setItems] = useState([]);
  const [formFor, setFormFor] = useState(null);
  const load = () => api.get('/admin/lester/external-finds').then((r) => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const act = async (id, action) => {
    try {
      await api.post(`/admin/lester/external-finds/${id}/${action}`, {});
      if (action === 'teach') window.dispatchEvent(new Event('lester-teachings-changed'));
    } catch (e) { /* keep item visible so admin can retry */ }
    setFormFor(null); load();
  };
  return (
    <div className="bg-white border border-amber-300/60 rounded-2xl p-6" data-testid="lester-external-panel">
      <h2 className="font-display text-xl mb-1 flex items-center gap-2"><Globe size={18} className="text-amber-600" /> External web finds {items.length > 0 && <span className="text-xs bg-amber-100 text-amber-800 rounded-full px-2 py-0.5 font-sans font-semibold">{items.length} new</span>}</h2>
      <p className="text-xs text-muted mb-4">When Lester couldn't answer from the site and looked at the web instead, the answer is flagged here. Turn a genuine find into a pending event or a teaching note — or dismiss it.</p>
      {items.length === 0 && <p className="text-sm text-muted">Nothing flagged — Lester has been answering from the site's own data.</p>}
      <ul className="space-y-3">
        {items.map((f) => (
          <li key={f.id} className="border border-charcoal/10 rounded-xl p-4" data-testid={`external-find-${f.id}`}>
            <div className="text-sm font-semibold mb-1">“{f.question}”</div>
            <p className="text-sm text-charcoal/80 mb-2">{f.answer}</p>
            {(f.sources || []).length > 0 && (
              <div className="flex flex-wrap gap-x-3 mb-2">
                {f.sources.map((s) => (
                  <a key={s} href={s} target="_blank" rel="noopener noreferrer" className="text-[11px] text-amber-700 underline truncate max-w-[260px]">{s}</a>
                ))}
              </div>
            )}
            <div className="text-[11px] text-muted mb-3">{new Date(f.created_at).toLocaleString('en-GB')}</div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setFormFor(formFor === f.id ? null : f.id)} data-testid={`find-create-event-${f.id}`}
                className="bg-forest text-base text-xs font-semibold px-4 py-1.5 rounded-full">Create draft event</button>
              <button onClick={() => act(f.id, 'teach')} data-testid={`find-teach-${f.id}`}
                className="bg-heritage/10 text-heritage text-xs font-semibold px-4 py-1.5 rounded-full hover:bg-heritage/20">Add as teaching</button>
              <button onClick={() => act(f.id, 'dismiss')} data-testid={`find-dismiss-${f.id}`}
                className="text-xs text-muted px-3 py-1.5 hover:text-heritage">Dismiss</button>
            </div>
            {formFor === f.id && <CreateEventForm find={f} onDone={() => { setFormFor(null); load(); }} />}
          </li>
        ))}
      </ul>
    </div>
  );
}

function TeachingsPanel() {
  const [items, setItems] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const load = () => api.get('/admin/lester/teachings').then((r) => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => {
    load();
    window.addEventListener('lester-teachings-changed', load);
    return () => window.removeEventListener('lester-teachings-changed', load);
  }, []);
  const add = async () => {
    if (text.trim().length < 3) return;
    setBusy(true);
    try { await api.post('/admin/lester/teachings', { text: text.trim() }); setText(''); await load(); } catch (e) { /* noop */ }
    setBusy(false);
  };
  const remove = async (id) => { await api.delete(`/admin/lester/teachings/${id}`); load(); };
  return (
    <div className="bg-white border border-charcoal/10 rounded-2xl p-6" data-testid="lester-teachings-panel">
      <h2 className="font-display text-xl mb-1 flex items-center gap-2"><GraduationCap size={18} className="text-heritage" /> Teach Lester</h2>
      <p className="text-xs text-muted mb-4">Notes here are treated as ground truth and override everything else. Use them to correct mistakes or add local knowledge, e.g. "The Space Centre car park is £4, book tickets online for a discount".</p>
      <div className="flex gap-2 mb-4">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} maxLength={800}
          placeholder="Add a fact or correction for Lester…" data-testid="lester-teaching-input"
          className="flex-1 bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-heritage" />
        <button onClick={add} disabled={busy || text.trim().length < 3} data-testid="lester-teaching-add"
          className="self-end bg-forest text-base text-sm font-semibold px-5 py-2.5 rounded-full disabled:opacity-50">Add</button>
      </div>
      <ul className="space-y-2">
        {items.map((t) => (
          <li key={t.id} className="flex items-start gap-3 text-sm bg-admin rounded-xl px-4 py-2.5" data-testid={`lester-teaching-${t.id}`}>
            <span className="flex-1">{t.text}</span>
            <span className="text-[11px] text-muted whitespace-nowrap">{new Date(t.created_at).toLocaleDateString('en-GB')}</span>
            <button onClick={() => remove(t.id)} data-testid={`lester-teaching-delete-${t.id}`} className="text-heritage hover:bg-heritage/10 rounded p-1"><Trash2 size={14} /></button>
          </li>
        ))}
        {items.length === 0 && <li className="text-sm text-muted">No teachings yet.</li>}
      </ul>
    </div>
  );
}

export default function AdminLester() {
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  useEffect(() => {
    api.get('/admin/lester/conversations').then((r) => {
      setSessions(r.data.sessions || []);
      if ((r.data.sessions || []).length) setActive(r.data.sessions[0].session_id);
    }).catch(() => {});
  }, []);
  const current = sessions.find((s) => s.session_id === active);
  return (
    <div data-testid="admin-lester-page">
      <div className="label text-heritage mb-2">Chatbot</div>
      <h1 className="font-display text-4xl mb-2 flex items-center gap-3"><MessageCircle size={28} /> Lester</h1>
      <p className="text-sm text-muted mb-8 max-w-2xl">Every public conversation with Lester is logged here. Read what visitors ask, check his answers, and use the teaching notes to make him smarter.</p>

      <div className="mb-8 space-y-6"><ExternalFindsPanel /><TeachingsPanel /></div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bg-white border border-charcoal/10 rounded-2xl p-4 max-h-[560px] overflow-y-auto" data-testid="lester-sessions-list">
          <div className="text-xs font-semibold uppercase tracking-wider text-charcoal/50 mb-3 px-2">Conversations ({sessions.length})</div>
          {sessions.length === 0 && <p className="text-sm text-muted px-2">No conversations yet — Lester is live on the public site, bottom-right.</p>}
          <ul className="space-y-1">
            {sessions.map((s) => (
              <li key={s.session_id}>
                <button onClick={() => setActive(s.session_id)} data-testid={`lester-session-${s.session_id}`}
                  className={`w-full text-left rounded-xl px-3 py-2.5 text-sm ${active === s.session_id ? 'bg-heritage/10 border border-heritage/30' : 'hover:bg-admin'}`}>
                  <div className="font-medium truncate">{(s.messages.find((m) => m.role === 'user') || {}).text || '(no question)'}</div>
                  <div className="text-[11px] text-muted">{new Date(s.last_at).toLocaleString('en-GB')} · {s.count} messages</div>
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-2 bg-white border border-charcoal/10 rounded-2xl p-5 max-h-[560px] overflow-y-auto" data-testid="lester-conversation-view">
          {!current && <p className="text-sm text-muted">Select a conversation.</p>}
          {current && (
            <div className="space-y-3">
              {current.messages.map((m) => (
                <div key={m.id} className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === 'user' ? 'ml-auto bg-forest text-base rounded-br-md' : 'bg-admin border border-charcoal/10 rounded-bl-md'}`}>
                  <div className="text-[10px] uppercase tracking-wider opacity-60 mb-0.5">{m.role === 'user' ? 'Visitor' : 'Lester'} · {new Date(m.created_at).toLocaleTimeString('en-GB')}
                    {m.external && <span className="ml-2 normal-case bg-amber-100 text-amber-800 rounded-full px-2 py-0.5 font-semibold opacity-100">Web-sourced</span>}
                  </div>
                  {m.text}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
